import { createParser, EventSourceMessage, ParseError } from "eventsource-parser";

/**
 * SSE message format
 */
export interface SSEMessage {
  /**
   * The event type (undefined for default events)
   */
  event?: string;

  /**
   * The data payload as a string
   */
  data: string;
}

/**
 * Options for handling SSE streams
 */
export interface HandleSSEStreamOptions {
  /**
   * The readable stream reader from the response body
   */
  reader: ReadableStreamDefaultReader<Uint8Array>;

  /**
   * Callback for handling parsed events
   */
  onEvent: (message: SSEMessage) => void;

  /**
   * Optional callback for handling parse errors
   */
  onError?: (error: Error) => void;

  /**
   * Optional callback for handling retry events
   */
  onRetry?: (retryInterval: number) => void;

  /**
   * Optional AbortSignal for cancellation
   */
  signal?: AbortSignal;
}

/**
 * Generic helper to handle Server-Sent Events streams
 * Reads from a ReadableStreamDefaultReader and feeds data to an SSE parser
 *
 * @param options - Configuration for handling the SSE stream
 * @throws Will throw if the stream encounters an error (other than AbortError)
 *
 * @example
 * ```typescript
 * const reader = response.body.getReader();
 * await handleSSEStream({
 *   reader,
 *   onEvent: (message) => {
 *     if (message.event === undefined) {
 *       // Default event
 *       const data = JSON.parse(message.data);
 *       console.log(data);
 *     }
 *   },
 *   onError: (error) => console.error("Parse error:", error),
 *   signal: abortController.signal
 * });
 * ```
 */
export async function handleSSEStream({
  reader,
  onEvent,
  onError,
  onRetry,
  signal,
}: HandleSSEStreamOptions): Promise<void> {
  // Create the SSE parser with provided callbacks
  const parser = createParser({
    onEvent: (message: EventSourceMessage) => {
      // Convert EventSourceMessage to SSEMessage
      const sseMessage: SSEMessage = {
        event: message.event,
        data: message.data,
      };
      onEvent(sseMessage);
    },
    onError: onError
      ? (error: ParseError) => onError(new Error(error.message))
      : (error: ParseError) => {
          console.error("SSE parser error:", error);
        },
    onRetry:
      onRetry ||
      ((retryInterval: number) => {
        console.log("SSE retry interval:", retryInterval);
      }),
  });

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      if (signal?.aborted) break;

      const chunk = new TextDecoder().decode(value, { stream: true });
      parser.feed(chunk);
    }
  } catch (error: unknown) {
    if (!(error instanceof Error)) {
      console.error("Unknown SSE stream error", error);
    } else if (error.name !== "AbortError") {
      console.error("Error in SSE stream:", error);
    }
    throw error;
  } finally {
    reader.releaseLock();
  }
}

/**
 * Helper to format SSE events for streaming
 * @param event - Optional event type (omit for default "message" events)
 * @param data - Data to send (will be JSON stringified)
 * @returns Formatted SSE string
 */
export function formatSSEEvent<T>(event: string | undefined, data: T): string {
  const lines: string[] = [];

  if (event) {
    lines.push(`event: ${event}`);
  }

  lines.push(`data: ${JSON.stringify(data)}`);

  lines.push(""); // Empty line to signal end of event
  return lines.join("\n") + "\n";
}
